import React, { useEffect, useMemo, useState } from "react";
import { API, graphqlOperation, Auth } from "aws-amplify";
import {
  getCompanyProfile,
  listCompanyProfiles,
  listCompanySubscriptions,
  listPrivateScreenings,
} from "../../graphql/queries";
import {
  updateCompanyProfile,
  updateCompanySubscription,
} from "../../graphql/mutations";
import Header from "../../components/header/Header";

const CompanyProfile = () => {
  const [companyProfile, setCompanyProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const [totalScans, setTotalScans] = useState(0);
  const [transactionHistory, setTransactionHistory] = useState([]);
  const [packageHistory, setPackageHistory] = useState([]);
  const [showUpcomingPackage, setShowUpcomingPackage] = useState(false);

  const companyName = companyProfile?.companyName || "";
  const companyEmail = companyProfile?.email || "";
  const companyUrl = companyProfile?.companyUrl || "";
  const contactPersonName = companyProfile?.contactPersonName || "";
  const contactPersonDesignation = companyProfile?.contactPersonDesignation || "";
  const contactPersonNumber = companyProfile?.contactPersonNumber || "";

  const currentPackage = useMemo(() => {
    const planName = companyProfile?.CompanySubscriptionPlan || "Trial";
    const start = companyProfile?.CompanySubscriptionStartDate
      ? new Date(companyProfile.CompanySubscriptionStartDate).toLocaleDateString()
      : "";
    const end = companyProfile?.CompanySubscriptionEndDate
      ? new Date(companyProfile.CompanySubscriptionEndDate).toLocaleDateString()
      : "";
    return { name: planName, startDate: start, endDate: end };
  }, [companyProfile]);

  const currentPoints = companyProfile?.remainingCredit ?? 0;

  useEffect(() => {
    const resolveCompanyProfileId = async () => {
      const storedId = localStorage.getItem("CompanyProfileID");
      if (storedId) return storedId;

      const storedCompanyProfile = localStorage.getItem("CompanyProfile");
      if (storedCompanyProfile) {
        try {
          const parsed = JSON.parse(storedCompanyProfile);
          if (parsed?.id) return parsed.id;
        } catch (e) {
          // ignore
        }
      }

      // Last resort: lookup by authenticated email (or candidateProfileEmailID)
      let emailToUse = null;
      try {
        const currentUser = await Auth.currentAuthenticatedUser();
        emailToUse = currentUser?.attributes?.email || null;
      } catch (e) {
        // ignore
      }
      emailToUse = emailToUse || localStorage.getItem("candidateProfileEmailID");
      if (!emailToUse) return null;

      const listRes = await API.graphql(
        graphqlOperation(listCompanyProfiles, {
          filter: { email: { eq: emailToUse } },
          limit: 1,
        })
      );

      const found = listRes?.data?.listCompanyProfiles?.items?.[0];
      if (found?.id) {
        localStorage.setItem("CompanyProfileID", found.id);
        localStorage.setItem("CompanyEmailID", found.email || emailToUse);
        localStorage.setItem("CompanyProfile", JSON.stringify(found));
        return found.id;
      }
      return null;
    };

    const fetchAll = async () => {
      setLoading(true);
      setLoadError("");
      try {
        // Prefer cached profile for immediate display
        const cached = localStorage.getItem("CompanyProfile");
        if (cached) {
          try {
            const parsed = JSON.parse(cached);
            if (parsed?.id) setCompanyProfile(parsed);
          } catch (e) {
            // ignore
          }
        }

        const companyProfileID = await resolveCompanyProfileId();
        if (!companyProfileID) {
          setLoadError("Company profile not found for this account.");
          return;
        }

        const response = await API.graphql(
          graphqlOperation(getCompanyProfile, { id: companyProfileID })
        );
        const profile = response?.data?.getCompanyProfile || null;
        if (profile) {
          setCompanyProfile(profile);
          localStorage.setItem("CompanyProfileID", profile.id);
          if (profile.email) localStorage.setItem("CompanyEmailID", profile.email);
          localStorage.setItem("CompanyProfile", JSON.stringify(profile));
        }

        const screeningsResponse = await API.graphql(
          graphqlOperation(listPrivateScreenings, {
            filter: { companyProfileID: { eq: companyProfileID } },
            limit: 1000,
          })
        );
        const screenings = screeningsResponse?.data?.listPrivateScreenings?.items || [];
        setTotalScans(screenings.length);

        const transactions = screenings
          .slice()
          .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
          .map((screening) => ({
            date: screening.createdAt ? new Date(screening.createdAt).toLocaleString() : "",
            name: screening.candidateName || "",
            email: screening.candidateEmail || "",
            deduction: screening.pointsDeducted ?? "",
          }));
        setTransactionHistory(transactions);

        const subscriptionRes = await API.graphql(
          graphqlOperation(listCompanySubscriptions, {
            filter: { companyProfileID: { eq: companyProfileID } },
            limit: 1000,
          })
        );
        const packages = subscriptionRes?.data?.listCompanySubscriptions?.items || [];
        packages.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        setPackageHistory(packages);
      } catch (error) {
        console.error("Error fetching company profile:", error);
        setLoadError("Failed to load company profile.");
      } finally {
        setLoading(false);
      }
    };

    fetchAll();
  }, []);

  const handleButtonClick = (isUpcoming) => {
    setShowUpcomingPackage(isUpcoming);
  };

  const handleActivatePackage = async (pkg) => {
    const confirmation = window.confirm(
      "Are you sure you want to activate this package?"
    );
    if (!confirmation) return;

    const startDate = new Date();
    const endDate = new Date(startDate);
    endDate.setMonth(endDate.getMonth() + parseInt(pkg.duration));

    const companyProfileID = localStorage.getItem("CompanyProfileID");

    try {
      await API.graphql(
        graphqlOperation(updateCompanyProfile, {
          input: {
            id: companyProfileID,
            CompanySubscriptionStartDate: startDate.toISOString(),
            CompanySubscriptionEndDate: endDate.toISOString(),
          },
        })
      );

      await API.graphql(
        graphqlOperation(updateCompanySubscription, {
          input: {
            id: pkg.id,
            startDate: startDate.toISOString(),
            endDate: endDate.toISOString(),
          },
        })
      );

      alert("Package activated successfully!");
    } catch (error) {
      console.error("Error activating package:", error);
      alert("Failed to activate the package. Please try again.");
    }
  };

  return (
    <div className="min-h-screen" style={{ 
      fontFamily: 'Sora, Inter, sans-serif',
      background: '#0f172a',
      color: '#F9FAFB'
    }}>
      <Header />

      {/* Background Effect */}
      <div className="fixed inset-0" style={{ zIndex: 0, pointerEvents: 'none' }}>
        <div 
          className="absolute inset-0"
          style={{
            background: 'linear-gradient(135deg, rgba(30, 41, 59, 0.4) 0%, rgba(15, 23, 42, 0.6) 100%)'
          }}
        />
      </div>

      <div className="relative z-10 w-full py-4 px-8" style={{ maxHeight: 'calc(100vh - 80px)', overflow: 'hidden' }}>
        {/* Header Section */}
        <div className="mb-3 text-left">
          <h1 className="text-2xl font-bold text-white m-0 mb-1" style={{ fontFamily: 'Sora, sans-serif' }}>
            Company Profile
          </h1>
          <h2 className="text-xs text-slate-400 m-0">
            View and manage your company information and subscription details
          </h2>
        </div>

        {loading && (
          <div className="text-center py-8">
            <div className="inline-block h-6 w-6 animate-spin rounded-full border-3 border-solid border-slate-500 border-r-transparent"></div>
            <p className="text-slate-400 mt-3 text-sm">Loading...</p>
          </div>
        )}

        {loadError && !loading && (
          <div className="p-3 rounded border border-slate-600 bg-slate-800/50 text-slate-300 text-sm">
            <p className="m-0">{loadError}</p>
          </div>
        )}

        {!loadError && !loading && (
          <div className="grid grid-cols-12 gap-3">
            {/* Left Column - Company Info and Credits */}
            <div className="col-span-5 space-y-3">
              {/* Credits Overview */}
              <div className="rounded-lg p-3 border border-slate-700/50"
                style={{
                  background: 'rgba(30, 41, 59, 0.5)',
                  backdropFilter: 'blur(10px)'
                }}>
                <h2 className="text-sm font-semibold text-white mb-3">Credits & Stats</h2>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <div className="text-xs text-slate-400 mb-1">Remaining Credits</div>
                    <div className="text-2xl font-bold text-white">{currentPoints}</div>
                  </div>
                  <div>
                    <div className="text-xs text-slate-400 mb-1">Total Screenings</div>
                    <div className="text-2xl font-bold text-white">{totalScans}</div>
                  </div>
                  <div>
                    <div className="text-xs text-slate-400 mb-1">Current Plan</div>
                    <div className="text-sm font-semibold text-white">{currentPackage.name}</div>
                    {currentPackage.startDate && currentPackage.endDate && (
                      <div className="text-xs text-slate-500 mt-1">
                        {currentPackage.startDate} - {currentPackage.endDate}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Company Information */}
              <div className="rounded-lg p-3 border border-slate-700/50"
                style={{
                  background: 'rgba(30, 41, 59, 0.5)',
                  backdropFilter: 'blur(10px)'
                }}>
                <h2 className="text-sm font-semibold text-white mb-3">Company Information</h2>
                <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
                  <div>
                    <div className="text-slate-400 mb-0.5">Company Name</div>
                    <div className="text-white font-medium">{companyName || "-"}</div>
                  </div>
                  <div>
                    <div className="text-slate-400 mb-0.5">Email Address</div>
                    <div className="text-white font-medium break-all">{companyEmail || "-"}</div>
                  </div>
                  <div>
                    <div className="text-slate-400 mb-0.5">Website</div>
                    <div className="text-white font-medium break-all">
                      {companyUrl ? (
                        <a href={companyUrl} target="_blank" rel="noopener noreferrer" className="hover:text-slate-300 transition-colors">
                          {companyUrl}
                        </a>
                      ) : "-"}
                    </div>
                  </div>
                  <div>
                    <div className="text-slate-400 mb-0.5">Contact Person</div>
                    <div className="text-white font-medium">{contactPersonName || "-"}</div>
                  </div>
                  <div>
                    <div className="text-slate-400 mb-0.5">Designation</div>
                    <div className="text-white font-medium">{contactPersonDesignation || "-"}</div>
                  </div>
                  <div>
                    <div className="text-slate-400 mb-0.5">Phone Number</div>
                    <div className="text-white font-medium">{contactPersonNumber || "-"}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Recent Screenings and Packages */}
            <div className="col-span-7 space-y-3">
              {/* Recent Screenings */}
              <div className="rounded-lg p-3 border border-slate-700/50"
                style={{
                  background: 'rgba(30, 41, 59, 0.5)',
                  backdropFilter: 'blur(10px)',
                  maxHeight: '200px'
                }}>
                <h2 className="text-sm font-semibold text-white mb-2">Recent Private Screenings</h2>
                <div className="overflow-auto" style={{ maxHeight: '150px' }}>
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-slate-800/80">
                      <tr className="border-b border-slate-700">
                        <th className="px-2 py-1.5 text-left font-semibold text-slate-400">Date</th>
                        <th className="px-2 py-1.5 text-left font-semibold text-slate-400">Candidate</th>
                        <th className="px-2 py-1.5 text-left font-semibold text-slate-400">Email</th>
                        <th className="px-2 py-1.5 text-left font-semibold text-slate-400">Credits</th>
                      </tr>
                    </thead>
                    <tbody>
                      {transactionHistory.length === 0 ? (
                        <tr>
                          <td className="px-2 py-4 text-center text-slate-400" colSpan={4}>
                            No screenings yet
                          </td>
                        </tr>
                      ) : (
                        transactionHistory.slice(0, 5).map((txn, index) => (
                          <tr key={index} className="border-b border-slate-700/30 hover:bg-slate-800/20">
                            <td className="px-2 py-1.5 text-slate-300">{txn.date}</td>
                            <td className="px-2 py-1.5 text-white font-medium">{txn.name}</td>
                            <td className="px-2 py-1.5 text-slate-400">{txn.email}</td>
                            <td className="px-2 py-1.5 text-white">{txn.deduction}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CompanyProfile;
